/**
 * Customer Portal (UI) - guided support request wizard.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { fetchCampaigns, fetchPhoneNumbers, submitSupportTicket } from '../features/api/client';
import type { AccountContextState } from '../features/account-context/account-context-store';
import type { FilterStore } from '../features/filters/filter-store';
import type {
  Campaign,
  PhoneNumber,
  ProvisioningCountry,
  ProvisioningNumberType,
  SupportTicketSubmitInput,
  TechnicalWebhookType,
  TechnicalIssueArea
} from '../types/portal';
import type { TimeDisplayMode } from '../utils/datetime';
import {
  getAreaCodesForUsState,
  validatePreferredAreaCode,
  USA_STATE_OPTIONS
} from '../../shared/usa-area-codes';

type SupportRouteProps = {
  accountContext: AccountContextState;
  filters: FilterStore;
  timeDisplayMode: TimeDisplayMode;
};

type SupportFamily = 'provisioning' | 'technical';
type WizardStep = 'details' | 'review';

type ProvisioningFormState = {
  numberType: ProvisioningNumberType;
  country: ProvisioningCountry;
  selectedUsState: string;
  preferredAreaCode: string;
  campaignMode: 'existing' | 'new_campaign';
  campaignId: string;
  notes: string;
};

type TechnicalFormState = {
  problemArea: TechnicalIssueArea;
  webhookType: TechnicalWebhookType;
  issueStartAt: string;
  issueEndAt: string;
  fromNumber: string;
  toNumber: string;
  messageId: string;
  description: string;
};

const DEFAULT_PROVISIONING_FORM: ProvisioningFormState = {
  numberType: '10DLC',
  country: 'USA',
  selectedUsState: '',
  preferredAreaCode: '',
  campaignMode: 'existing',
  campaignId: '',
  notes: ''
};

const DEFAULT_TECHNICAL_FORM: TechnicalFormState = {
  problemArea: 'sending',
  webhookType: 'dlr',
  issueStartAt: '',
  issueEndAt: '',
  fromNumber: '',
  toNumber: '',
  messageId: '',
  description: ''
};

function normalizePhoneValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }
  const digitsOnly = trimmed.replace(/\D/g, '');
  return digitsOnly ? `+${digitsOnly}` : '';
}

function formatFamilyLabel(family: SupportFamily): string {
  return family === 'provisioning' ? 'Provisioning' : 'Technical support';
}

export function SupportNewTicketRoute({ accountContext, filters, timeDisplayMode }: SupportRouteProps) {
  const accountId = accountContext.selectedAccountId;
  const [family, setFamily] = useState<SupportFamily>('provisioning');
  const [wizardStep, setWizardStep] = useState<WizardStep>('details');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [provisioningForm, setProvisioningForm] = useState<ProvisioningFormState>(DEFAULT_PROVISIONING_FORM);
  const [technicalForm, setTechnicalForm] = useState<TechnicalFormState>(DEFAULT_TECHNICAL_FORM);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoadingData(true);
    setError(null);
    Promise.all([fetchCampaigns(accountId), fetchPhoneNumbers(accountId, filters.filters)])
      .then(([campaignPayload, phonePayload]) => {
        if (cancelled) return;
        setCampaigns(campaignPayload.items);
        setPhoneNumbers(phonePayload.items);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingData(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, filters.filters]);

  const campaignById = useMemo(() => new Map(campaigns.map((campaign) => [campaign.id, campaign])), [campaigns]);
  const availablePhoneValues = useMemo(() => new Set(phoneNumbers.map((number) => number.e164)), [phoneNumbers]);

  function clearStatus(): void {
    setFieldErrors([]);
    setSuccessMessage(null);
    setError(null);
  }

  function validateCurrentForm(): string[] {
    const issues: string[] = [];
    if (family === 'provisioning') {
      if (!provisioningForm.numberType) {
        issues.push('Select a number type.');
      }
      if (!provisioningForm.country) {
        issues.push('Select a country.');
      }
      const areaCodeValidation = validatePreferredAreaCode({
        country: provisioningForm.country,
        preferredAreaCode: provisioningForm.preferredAreaCode,
        selectedUsState: provisioningForm.country === 'USA' ? provisioningForm.selectedUsState : null
      });
      if (areaCodeValidation.error) {
        issues.push(areaCodeValidation.error);
      }
      if (provisioningForm.campaignMode === 'existing' && !provisioningForm.campaignId) {
        issues.push('Select an existing campaign or choose "new campaign needed".');
      }
    } else {
      if (!technicalForm.problemArea) {
        issues.push('Select the problem area.');
      }
      if (!technicalForm.issueStartAt) {
        issues.push('Provide the issue start time.');
      }
      if (!technicalForm.issueEndAt) {
        issues.push('Provide the issue end time.');
      }
      if (technicalForm.issueStartAt && technicalForm.issueEndAt) {
        const startTs = Date.parse(technicalForm.issueStartAt);
        const endTs = Date.parse(technicalForm.issueEndAt);
        if (Number.isFinite(startTs) && Number.isFinite(endTs) && endTs < startTs) {
          issues.push('Issue end time must be after start time.');
        }
      }
      const normalizedFrom = normalizePhoneValue(technicalForm.fromNumber);
      const normalizedTo = normalizePhoneValue(technicalForm.toNumber);
      const hasKnownFrom = normalizedFrom.length > 0 && availablePhoneValues.has(normalizedFrom);
      const hasKnownTo = normalizedTo.length > 0 && availablePhoneValues.has(normalizedTo);

      if (technicalForm.problemArea === 'sending') {
        if (!hasKnownFrom) {
          issues.push('For sending issues, select a valid From phone number from your available numbers.');
        }
      } else if (technicalForm.problemArea === 'receiving') {
        if (!hasKnownTo) {
          issues.push('For receiving issues, select a valid To phone number from your available numbers.');
        }
      } else if (technicalForm.problemArea === 'webhooks') {
        if (technicalForm.webhookType !== 'dlr' && technicalForm.webhookType !== 'inbound_message') {
          issues.push('For webhook issues, select a webhook type.');
        } else if (technicalForm.webhookType === 'dlr') {
          if (!hasKnownFrom) {
            issues.push('For DLR webhook issues, select a valid From phone number from your available numbers.');
          }
        } else if (!hasKnownTo) {
          issues.push('For inbound message webhook issues, select a valid To phone number from your available numbers.');
        }
      }
      if (!technicalForm.description.trim()) {
        issues.push('Describe the issue before submitting.');
      }
    }
    return issues;
  }

  function buildPayload(): SupportTicketSubmitInput {
    if (family === 'provisioning') {
      const areaCodeValidation = validatePreferredAreaCode({
        country: provisioningForm.country,
        preferredAreaCode: provisioningForm.preferredAreaCode,
        selectedUsState: provisioningForm.country === 'USA' ? provisioningForm.selectedUsState : null
      });
      return {
        family: 'provisioning',
        numberType: provisioningForm.numberType,
        country: provisioningForm.country,
        selectedUsState: provisioningForm.country === 'USA' ? areaCodeValidation.normalizedUsState : null,
        preferredAreaCode: areaCodeValidation.normalizedAreaCode,
        campaignMode: provisioningForm.campaignMode,
        campaignId: provisioningForm.campaignMode === 'existing' ? provisioningForm.campaignId : null,
        notes: provisioningForm.notes.trim() || null
      };
    }
    return {
      family: 'technical',
      problemArea: technicalForm.problemArea,
      webhookType: technicalForm.problemArea === 'webhooks' ? technicalForm.webhookType : null,
      issueStartAt: technicalForm.issueStartAt,
      issueEndAt: technicalForm.issueEndAt,
      fromNumber: normalizePhoneValue(technicalForm.fromNumber) || null,
      toNumber: normalizePhoneValue(technicalForm.toNumber) || null,
      messageId: technicalForm.messageId.trim() || null,
      description: technicalForm.description.trim()
    };
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    clearStatus();
    const issues = validateCurrentForm();
    if (issues.length > 0) {
      setFieldErrors(issues);
      setWizardStep('details');
      return;
    }
    if (!accountId) {
      setError('No account selected.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const response = await submitSupportTicket(accountId, payload);
      const ticketDetails = response.ticketNumber
        ? `Ticket #${response.ticketNumber} created successfully.`
        : response.ticketId
          ? `Ticket ${response.ticketId} created successfully.`
          : response.message;
      setSuccessMessage(ticketDetails);
      setWizardStep('details');
      if (family === 'provisioning') {
        setProvisioningForm(DEFAULT_PROVISIONING_FORM);
      } else {
        setTechnicalForm(DEFAULT_TECHNICAL_FORM);
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  const selectedCampaign = provisioningForm.campaignId ? campaignById.get(provisioningForm.campaignId) ?? null : null;
  const usAreaCodesForSelectedState = useMemo(
    () => getAreaCodesForUsState(provisioningForm.selectedUsState),
    [provisioningForm.selectedUsState]
  );

  return (
    <>
      <div className="page-pretitle">Customer care</div>
      <h2 className="page-title mb-3">Support</h2>

      {accountContext.loading ? <div className="alert alert-info">Loading account context...</div> : null}
      {accountContext.error ? <div className="alert alert-danger">{accountContext.error}</div> : null}
      {!accountId ? <div className="alert alert-warning">No account selected.</div> : null}

      {successMessage ? <div className="alert alert-success">{successMessage}</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {fieldErrors.length > 0 ? (
        <div className="alert alert-warning">
          <div className="fw-semibold mb-2">Please fix the following before submitting:</div>
          <ul className="mb-0">
            {fieldErrors.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <form className="card" onSubmit={handleSubmit}>
        <div className="card-header">
          <h3 className="card-title mb-0">Step 1 - Request type</h3>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-selectgroup-item">
                <input
                  type="radio"
                  className="form-selectgroup-input"
                  name="supportFamily"
                  checked={family === 'provisioning'}
                  onChange={() => {
                    clearStatus();
                    setWizardStep('details');
                    setFamily('provisioning');
                  }}
                />
                <span className="form-selectgroup-label h-100 d-flex flex-column align-items-start">
                  <span className="fw-semibold">Provisioning</span>
                  <span className="text-secondary small">New number requests, campaign alignment, and number setup.</span>
                </span>
              </label>
            </div>
            <div className="col-md-6">
              <label className="form-selectgroup-item">
                <input
                  type="radio"
                  className="form-selectgroup-input"
                  name="supportFamily"
                  checked={family === 'technical'}
                  onChange={() => {
                    clearStatus();
                    setWizardStep('details');
                    setFamily('technical');
                  }}
                />
                <span className="form-selectgroup-label h-100 d-flex flex-column align-items-start">
                  <span className="fw-semibold">Technical support</span>
                  <span className="text-secondary small">Sending, receiving, and webhook delivery troubleshooting.</span>
                </span>
              </label>
            </div>
          </div>
        </div>

        {wizardStep === 'details' ? (
          <div className="card-body border-top">
            <h3 className="card-title">Step 2 - Request details</h3>
            {family === 'provisioning' ? (
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">
                    Number type
                    <select
                      className="form-select"
                      value={provisioningForm.numberType}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setProvisioningForm((current) => ({
                          ...current,
                          numberType: event.target.value as ProvisioningNumberType
                        }))
                      }
                    >
                      <option value="10DLC">10DLC</option>
                      <option value="SHORT_CODE">Short code</option>
                    </select>
                  </label>
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Country
                    <select
                      className="form-select"
                      value={provisioningForm.country}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setProvisioningForm((current) => ({
                          ...current,
                          country: event.target.value as ProvisioningCountry,
                          selectedUsState: event.target.value === 'USA' ? current.selectedUsState : '',
                          preferredAreaCode: event.target.value === 'UK' ? '' : current.preferredAreaCode
                        }))
                      }
                    >
                      <option value="USA">USA</option>
                      <option value="CANADA">Canada</option>
                      <option value="UK">United Kingdom</option>
                    </select>
                  </label>
                </div>
                {provisioningForm.country === 'USA' ? (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">
                        State (optional)
                        <select
                          className="form-select"
                          value={provisioningForm.selectedUsState}
                          disabled={loadingData || !accountId}
                          onChange={(event) =>
                            setProvisioningForm((current) => {
                              const selectedState = event.target.value;
                              const stateAreaCodes = getAreaCodesForUsState(selectedState);
                              const keepAreaCode =
                                current.preferredAreaCode.trim().length === 0 ||
                                stateAreaCodes.includes(current.preferredAreaCode.trim());
                              return {
                                ...current,
                                selectedUsState: selectedState,
                                preferredAreaCode: keepAreaCode ? current.preferredAreaCode : ''
                              };
                            })
                          }
                        >
                          <option value="">All states</option>
                          {USA_STATE_OPTIONS.map((stateCode) => (
                            <option key={stateCode} value={stateCode}>
                              {stateCode}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Area code from state (optional)
                        <select
                          className="form-select"
                          value={
                            usAreaCodesForSelectedState.includes(provisioningForm.preferredAreaCode)
                              ? provisioningForm.preferredAreaCode
                              : ''
                          }
                          disabled={loadingData || !accountId || !provisioningForm.selectedUsState}
                          onChange={(event) =>
                            setProvisioningForm((current) => ({
                              ...current,
                              preferredAreaCode: event.target.value
                            }))
                          }
                        >
                          <option value="">Select area code...</option>
                          {usAreaCodesForSelectedState.map((areaCode) => (
                            <option key={areaCode} value={areaCode}>
                              {areaCode}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Preferred area code (manual entry, optional)
                        <input
                          className="form-control"
                          value={provisioningForm.preferredAreaCode}
                          maxLength={3}
                          inputMode="numeric"
                          pattern="[0-9]{3}"
                          disabled={loadingData || !accountId}
                          onChange={(event) =>
                            setProvisioningForm((current) => ({
                              ...current,
                              preferredAreaCode: event.target.value.replace(/\D/g, '').slice(0, 3)
                            }))
                          }
                          placeholder="Example: 415"
                        />
                      </label>
                    </div>
                  </>
                ) : (
                  <div className="col-md-4">
                    <label className="form-label">
                      Preferred area code {provisioningForm.country === 'UK' ? '(not available for UK)' : '(optional)'}
                      <input
                        className="form-control"
                        value={provisioningForm.preferredAreaCode}
                        maxLength={3}
                        inputMode="numeric"
                        pattern="[0-9]{3}"
                        disabled={loadingData || !accountId || provisioningForm.country === 'UK'}
                        onChange={(event) =>
                          setProvisioningForm((current) => ({
                            ...current,
                            preferredAreaCode: event.target.value.replace(/\D/g, '').slice(0, 3)
                          }))
                        }
                        placeholder={provisioningForm.country === 'UK' ? 'Not available' : 'Example: 604'}
                      />
                    </label>
                  </div>
                )}
                <div className="col-md-12">
                  <label className="form-label d-block mb-2">Campaign context</label>
                  <div className="d-flex flex-wrap gap-3">
                    <label className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={provisioningForm.campaignMode === 'existing'}
                        onChange={() =>
                          setProvisioningForm((current) => ({
                            ...current,
                            campaignMode: 'existing'
                          }))
                        }
                      />
                      <span className="form-check-label">Use existing campaign</span>
                    </label>
                    <label className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={provisioningForm.campaignMode === 'new_campaign'}
                        onChange={() =>
                          setProvisioningForm((current) => ({
                            ...current,
                            campaignMode: 'new_campaign',
                            campaignId: ''
                          }))
                        }
                      />
                      <span className="form-check-label">New campaign needed</span>
                    </label>
                  </div>
                  {provisioningForm.campaignMode === 'new_campaign' ? (
                    <div className="text-secondary mt-2 small">
                      This request will be submitted as requiring a new campaign before activation.
                    </div>
                  ) : null}
                </div>
                {provisioningForm.campaignMode === 'existing' ? (
                  <div className="col-md-12">
                    <label className="form-label">
                      Existing campaign
                      <select
                        className="form-select"
                        value={provisioningForm.campaignId}
                        disabled={loadingData || !accountId}
                        onChange={(event) =>
                          setProvisioningForm((current) => ({
                            ...current,
                            campaignId: event.target.value
                          }))
                        }
                      >
                        <option value="">Select campaign...</option>
                        {campaigns.map((campaign) => (
                          <option key={campaign.id} value={campaign.id}>
                            {campaign.name} ({campaign.id})
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : null}
                <div className="col-md-12">
                  <label className="form-label">
                    Notes (optional)
                    <textarea
                      className="form-control"
                      rows={4}
                      value={provisioningForm.notes}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setProvisioningForm((current) => ({
                          ...current,
                          notes: event.target.value
                        }))
                      }
                      placeholder="Share volume estimates, launch date, or business context."
                    />
                  </label>
                </div>
              </div>
            ) : (
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Problem area
                    <select
                      className="form-select"
                      value={technicalForm.problemArea}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setTechnicalForm((current) => ({
                          ...current,
                          problemArea: event.target.value as TechnicalIssueArea,
                          webhookType:
                            event.target.value === 'webhooks' ? current.webhookType : 'dlr'
                        }))
                      }
                    >
                      <option value="sending">Sending</option>
                      <option value="receiving">Receiving</option>
                      <option value="webhooks">Webhooks</option>
                    </select>
                  </label>
                </div>
                {technicalForm.problemArea === 'webhooks' ? (
                  <div className="col-md-6">
                    <label className="form-label">
                      Webhook type
                      <select
                        className="form-select"
                        value={technicalForm.webhookType}
                        disabled={loadingData || !accountId}
                        onChange={(event) =>
                          setTechnicalForm((current) => ({
                            ...current,
                            webhookType: event.target.value as TechnicalWebhookType
                          }))
                        }
                      >
                        <option value="dlr">DLR</option>
                        <option value="inbound_message">Inbound message</option>
                      </select>
                    </label>
                  </div>
                ) : null}
                <div className="col-md-6">
                  <label className="form-label">
                    Issue start ({timeDisplayMode === 'utc' ? 'UTC' : 'Local'})
                    <input
                      className="form-control"
                      type="datetime-local"
                      value={technicalForm.issueStartAt}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setTechnicalForm((current) => ({
                          ...current,
                          issueStartAt: event.target.value
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Issue end ({timeDisplayMode === 'utc' ? 'UTC' : 'Local'})
                    <input
                      className="form-control"
                      type="datetime-local"
                      value={technicalForm.issueEndAt}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setTechnicalForm((current) => ({
                          ...current,
                          issueEndAt: event.target.value
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    {technicalForm.problemArea === 'sending' || (technicalForm.problemArea === 'webhooks' && technicalForm.webhookType === 'dlr')
                      ? 'From phone number (required)'
                      : 'From phone number (optional)'}
                    {(technicalForm.problemArea === 'sending' ||
                      (technicalForm.problemArea === 'webhooks' && technicalForm.webhookType === 'dlr')) ? (
                      <select
                        className="form-select"
                        value={technicalForm.fromNumber}
                        disabled={loadingData || !accountId}
                        onChange={(event) =>
                          setTechnicalForm((current) => ({
                            ...current,
                            fromNumber: event.target.value
                          }))
                        }
                      >
                        <option value="">Select From number...</option>
                        {phoneNumbers.map((phoneNumber) => (
                          <option key={phoneNumber.id} value={phoneNumber.e164}>
                            {phoneNumber.label} ({phoneNumber.e164})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="form-control"
                        value={technicalForm.fromNumber}
                        disabled={loadingData || !accountId}
                        onChange={(event) =>
                          setTechnicalForm((current) => ({
                            ...current,
                            fromNumber: event.target.value
                          }))
                        }
                        placeholder="+15551234567"
                      />
                    )}
                  </label>
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    {technicalForm.problemArea === 'receiving' ||
                    (technicalForm.problemArea === 'webhooks' && technicalForm.webhookType === 'inbound_message')
                      ? 'To phone number (required)'
                      : 'To phone number (optional)'}
                    {(technicalForm.problemArea === 'receiving' ||
                      (technicalForm.problemArea === 'webhooks' && technicalForm.webhookType === 'inbound_message')) ? (
                      <select
                        className="form-select"
                        value={technicalForm.toNumber}
                        disabled={loadingData || !accountId}
                        onChange={(event) =>
                          setTechnicalForm((current) => ({
                            ...current,
                            toNumber: event.target.value
                          }))
                        }
                      >
                        <option value="">Select To number...</option>
                        {phoneNumbers.map((phoneNumber) => (
                          <option key={phoneNumber.id} value={phoneNumber.e164}>
                            {phoneNumber.label} ({phoneNumber.e164})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="form-control"
                        value={technicalForm.toNumber}
                        disabled={loadingData || !accountId}
                        onChange={(event) =>
                          setTechnicalForm((current) => ({
                            ...current,
                            toNumber: event.target.value
                          }))
                        }
                        placeholder="+15557654321"
                      />
                    )}
                  </label>
                </div>
                <div className="col-md-12">
                  <label className="form-label">
                    Message ID (optional)
                    <input
                      className="form-control"
                      value={technicalForm.messageId}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setTechnicalForm((current) => ({
                          ...current,
                          messageId: event.target.value
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="col-md-12">
                  <label className="form-label">
                    Description
                    <textarea
                      className="form-control"
                      rows={5}
                      value={technicalForm.description}
                      disabled={loadingData || !accountId}
                      onChange={(event) =>
                        setTechnicalForm((current) => ({
                          ...current,
                          description: event.target.value
                        }))
                      }
                      placeholder="Describe the observed behavior, expected behavior, and impact."
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="card-body border-top">
            <h3 className="card-title">Step 3 - Review</h3>
            <div className="mb-2">
              <span className="text-secondary">Request type:</span> {formatFamilyLabel(family)}
            </div>
            {family === 'provisioning' ? (
              <dl className="row mb-0">
                <dt className="col-sm-3">Number type</dt>
                <dd className="col-sm-9">{provisioningForm.numberType === 'SHORT_CODE' ? 'Short code' : '10DLC'}</dd>
                <dt className="col-sm-3">Country</dt>
                <dd className="col-sm-9">{provisioningForm.country}</dd>
                {provisioningForm.country === 'USA' ? (
                  <>
                    <dt className="col-sm-3">State</dt>
                    <dd className="col-sm-9">{provisioningForm.selectedUsState || 'All states'}</dd>
                  </>
                ) : null}
                <dt className="col-sm-3">Preferred area code</dt>
                <dd className="col-sm-9">{provisioningForm.preferredAreaCode.trim() || 'Not specified'}</dd>
                <dt className="col-sm-3">Campaign</dt>
                <dd className="col-sm-9">
                  {provisioningForm.campaignMode === 'new_campaign'
                    ? 'New campaign required'
                    : selectedCampaign
                      ? `${selectedCampaign.name} (${selectedCampaign.id})`
                      : 'Not selected'}
                </dd>
                <dt className="col-sm-3">Notes</dt>
                <dd className="col-sm-9">{provisioningForm.notes.trim() || 'None'}</dd>
              </dl>
            ) : (
              <dl className="row mb-0">
                <dt className="col-sm-3">Problem area</dt>
                <dd className="col-sm-9 text-capitalize">{technicalForm.problemArea}</dd>
                {technicalForm.problemArea === 'webhooks' ? (
                  <>
                    <dt className="col-sm-3">Webhook type</dt>
                    <dd className="col-sm-9">{technicalForm.webhookType}</dd>
                  </>
                ) : null}
                <dt className="col-sm-3">Issue window</dt>
                <dd className="col-sm-9">
                  {technicalForm.issueStartAt || 'N/A'} to {technicalForm.issueEndAt || 'N/A'}
                </dd>
                <dt className="col-sm-3">From / To</dt>
                <dd className="col-sm-9">
                  {normalizePhoneValue(technicalForm.fromNumber) || 'N/A'} {'->'} {normalizePhoneValue(technicalForm.toNumber) || 'N/A'}
                </dd>
                <dt className="col-sm-3">Message ID</dt>
                <dd className="col-sm-9">{technicalForm.messageId.trim() || 'None'}</dd>
                <dt className="col-sm-3">Description</dt>
                <dd className="col-sm-9">{technicalForm.description.trim() || 'None'}</dd>
              </dl>
            )}
            <div className="text-secondary small mt-3">
              Ticket will include account {accountId ?? 'N/A'} and requester {accountContext.session?.userEmail ?? accountContext.session?.userId ?? 'unknown'}.
            </div>
          </div>
        )}

        <div className="card-footer d-flex gap-2 justify-content-between">
          <div className="text-secondary small align-self-center">
            {loadingData ? 'Loading campaign and phone context...' : 'Submitting creates a support ticket immediately.'}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary" type="submit" disabled={loadingData || submitting || !accountId}>
              {submitting ? 'Submitting...' : 'Create support ticket'}
            </button>
          </div>
        </div>
      </form>
    </>
  );
}
